import { useRef, useState } from "react";
import { GripVertical, RotateCcw } from "lucide-react";
import { Button } from "@/components/ui/button";
import { resetPrototype } from "./mock";

/**
 * Prototype-only affordance: a floating "Reset prototype" button that wipes all
 * mock data back to the seeded default state. Rendered top-left on a white
 * background and draggable (grip handle) so it can be moved out of the way of
 * whatever is being demoed. Mounted globally but only when the dev auth bypass
 * is active (see App.tsx).
 */
const POS_KEY = "tcf-reset-button-pos";

function loadPos(): { x: number; y: number } {
  try {
    const raw = localStorage.getItem(POS_KEY);
    if (raw) return JSON.parse(raw);
  } catch {
    /* fall through to default */
  }
  return { x: 12, y: 12 };
}

export function PrototypeResetButton() {
  const [pos, setPos] = useState(loadPos);
  const posRef = useRef(pos);
  const drag = useRef<{ sx: number; sy: number; ox: number; oy: number } | null>(null);

  const onPointerDown = (e: React.PointerEvent) => {
    (e.currentTarget as HTMLElement).setPointerCapture(e.pointerId);
    drag.current = { sx: e.clientX, sy: e.clientY, ox: pos.x, oy: pos.y };
  };

  const onPointerMove = (e: React.PointerEvent) => {
    const d = drag.current;
    if (!d) return;
    const x = Math.max(0, Math.min(window.innerWidth - 80, d.ox + (e.clientX - d.sx)));
    const y = Math.max(0, Math.min(window.innerHeight - 40, d.oy + (e.clientY - d.sy)));
    posRef.current = { x, y };
    setPos(posRef.current);
  };

  const onPointerUp = (e: React.PointerEvent) => {
    (e.currentTarget as HTMLElement).releasePointerCapture?.(e.pointerId);
    if (drag.current) localStorage.setItem(POS_KEY, JSON.stringify(posRef.current));
    drag.current = null;
  };

  const handleReset = () => {
    resetPrototype();
    window.location.reload();
  };

  return (
    <div
      className="fixed z-[100] flex items-center gap-1 rounded-md border border-border bg-background px-1.5 py-1 shadow-md"
      style={{ left: pos.x, top: pos.y }}
    >
      <button
        type="button"
        aria-label="Drag to move"
        onPointerDown={onPointerDown}
        onPointerMove={onPointerMove}
        onPointerUp={onPointerUp}
        className="flex cursor-grab touch-none items-center text-muted-foreground active:cursor-grabbing"
      >
        <GripVertical className="size-4" />
      </button>
      <Button type="button" variant="ghost" size="sm" className="pl-2" onClick={handleReset}>
        <RotateCcw className="size-4 text-muted-foreground" />
        Reset prototype
      </Button>
    </div>
  );
}
